import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
  TextField,
} from "@material-ui/core";
import React from "react";
import { makeStyles } from "@material-ui/styles";
import { Formik } from "formik";
import CloseIcon from "@material-ui/icons/CloseRounded";
import { groupByCategory } from "../../utils/bundle-modules";
import ConfirmationDialog from "../../../../components/Dialog/confirmation";

const useStyle = makeStyles((theme) => ({
  scrollContainer: {
    maxHeight: "70vh",
    overflowY: "auto",
    backgroundColor: "#f3f3f3",
  },
  moduleCard: {
    padding: theme.spacing(1.5),
    height: "100%",
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,.12)",
  },
  moduleCardOn: {
    padding: theme.spacing(1.5),
    height: "100%",
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,.12)",
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(25,118,210,.08)",
  },
  badge: {
    marginLeft: theme.spacing(0.5),
  },
}));

const validate = (values) => {
  const errors = {};
  if (!values.name || !values.name.trim()) {
    errors.name = "Name is required";
  }
  return errors;
};

// "Save as new" creates a second bundle, and the backend rejects a name already held by an
// active bundle. Compare byte-for-byte against what was loaded so ANY edit re-enables the
// button and the server keeps the final say — this is a hint, not a second validator.
const isNameUnchanged = (values, record) => values.name === (record.name || "");

export default function BundleEditDialogComponent({
  moduleListing,
  open,
  onClose,
  onSubmit,
  onSubmitAsNew,
  isLoading,
  companyCount,
  countPending,
  countFailed,
  onRetryCount,
  record,
  canEdit,
}) {
  const classes = useStyle();
  const grantedRights = (record.keys || []).length;
  // The blast-radius count is trustworthy only once it has resolved for this bundle.
  const countKnown = !countPending && !countFailed;

  // An in-place Save re-flattens every company on this bundle, REPLACING their effective
  // access (#5). When there is at least one such company the admin must confirm first; the
  // values are held until they do. This gate sits on the FORM submit, not just the button,
  // so Enter cannot slip a save past it.
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState(null);

  const submitWithConfirm = (values, helpers) => {
    if (helpers) {
      helpers.setSubmitting(false);
    }
    // Fail closed: never let a save through while the blast radius is unknown.
    if (!countKnown) {
      return;
    }
    if (companyCount > 0) {
      setPendingValues(values);
      setConfirmOpen(true);
      return;
    }
    onSubmit(values);
  };

  const handleConfirmProceed = () => {
    setConfirmOpen(false);
    if (pendingValues) {
      onSubmit(pendingValues);
      setPendingValues(null);
    }
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setPendingValues(null);
  };

  // The two fail-closed tooltip states; empty once the count is known so Save reads plainly.
  let saveTooltip = "";
  if (countPending) {
    saveTooltip = "Verifying affected companies…";
  } else if (countFailed) {
    saveTooltip = "Couldn't verify affected companies — retry";
  }

  // "Save as new" is deliberately NOT the form's submit button: Enter still means Save (edit
  // in place), which is what this dialog has always done. Run the same validation Formik
  // would run on submit, then hand the values to the create path.
  const handleSaveAsNew = (formik) => {
    formik.setFieldTouched("name", true, false);
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        onSubmitAsNew(formik.values);
      }
    });
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      fullWidth
      PaperProps={{
        style: {
          maxWidth: '1200px',
        },
      }}
    >
      <DialogTitle id="scroll-dialog-title">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2">
            <b>Edit Bundle</b>
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={onClose}
            style={{ padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Formik
        initialValues={{
          name: record.name || "",
          description: record.description || "",
          moduleIds: record.moduleIds || [],
        }}
        validate={validate}
        onSubmit={submitWithConfirm}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <DialogContent dividers className={classes.scrollContainer}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                variant="outlined"
                value={formik.values.name}
                error={Boolean(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                disabled={!canEdit}
              />
              <Box pt={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description"
                  name="description"
                  variant="outlined"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  disabled={!canEdit}
                />
              </Box>
              <Box py={2}>
                <Divider />
              </Box>
              <Box pb={1}>
                <Typography variant="body2">
                  <b>Modules</b>
                  {" "}
                  <Typography variant="caption" color="textSecondary" component="span">
                    {formik.values.moduleIds.length}
                    {" selected, grants "}
                    {grantedRights}
                    {" rights"}
                  </Typography>
                </Typography>
              </Box>
              {groupByCategory(moduleListing).map(([category, mods]) => (
                <Box key={category} mb={2}>
                  <Typography variant="overline" color="textSecondary">
                    {category}
                    {" "}
                    {mods.filter((m) => formik.values.moduleIds.includes(m.id)).length}
                    {" of "}
                    {mods.length}
                    {" on"}
                  </Typography>
                  <Grid container spacing={1}>
                    {mods.map((m) => {
                      const on = formik.values.moduleIds.includes(m.id);
                      return (
                        <Grid item xs={12} sm={6} key={m.id}>
                          <Paper
                            variant="outlined"
                            className={on ? classes.moduleCardOn : classes.moduleCard}
                            onClick={() => {
                              if (!canEdit) {
                                return;
                              }
                              formik.setFieldValue(
                                "moduleIds",
                                on
                                  ? formik.values.moduleIds.filter((x) => x !== m.id)
                                  : [...formik.values.moduleIds, m.id]
                              );
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body2">
                                  {m.name}
                                  {m.tier === "addon" && <Chip size="small" label="add-on" className={classes.badge} />}
                                  {m.tier === "client" && <Chip size="small" label="client" className={classes.badge} />}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">{m.description}</Typography>
                              </Box>
                              <Switch checked={on} disabled={!canEdit} readOnly />
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </DialogContent>
            {canEdit && (
              <>
                {/* The blast radius of an in-place Save. Silent at zero — nothing to warn
                    about. Withheld until the count is KNOWN, so a stale or in-flight number
                    is never presented as the answer. */}
                {countKnown && companyCount > 0 && (
                  <Box px={3} pt={2}>
                    <Typography variant="caption" color="textSecondary">
                      {`Save will update ${companyCount} company(ies) currently on this bundle. Save as new leaves them unchanged.`}
                    </Typography>
                  </Box>
                )}
                {countPending && (
                  <Box px={3} pt={2}>
                    <Typography variant="caption" color="textSecondary">
                      Verifying affected companies…
                    </Typography>
                  </Box>
                )}
                {countFailed && (
                  <Box px={3} pt={2} display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Typography variant="caption" color="error">
                      Couldn&apos;t verify affected companies — retry.
                    </Typography>
                    <Button size="small" variant="text" onClick={onRetryCount}>
                      Retry
                    </Button>
                  </Box>
                )}
                <DialogActions>
                  <Button
                    variant="text"
                    type="button"
                    disabled={isLoading}
                    onClick={onClose}
                    style={{ color: "black" }}
                  >
                    Cancel
                  </Button>
                  {/* Tooltip needs a non-disabled child to receive pointer events, hence the span. */}
                  <Tooltip
                    title={isNameUnchanged(formik.values, record)
                      ? "Give it a different name — a new bundle cannot reuse this one's name."
                      : ""}
                  >
                    <span>
                      <Button
                        variant="outlined"
                        type="button"
                        disabled={isLoading || isNameUnchanged(formik.values, record)}
                        onClick={() => handleSaveAsNew(formik)}
                      >
                        Save as new
                      </Button>
                    </span>
                  </Tooltip>
                  {/* Fail closed: Save is disabled — and the form-level gate refuses — until
                      the blast-radius count has resolved for this bundle. */}
                  <Tooltip title={saveTooltip}>
                    <span>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={isLoading || !countKnown}
                      >
                        Save
                      </Button>
                    </span>
                  </Tooltip>
                </DialogActions>
              </>
            )}
          </form>
        )}
      </Formik>
    </Dialog>
    <ConfirmationDialog
      isOpen={confirmOpen}
      handleClose={handleConfirmClose}
      handleProceed={handleConfirmProceed}
      title={`Saving will REPLACE the effective access of ${companyCount} company(ies) currently on this bundle.`}
    />
    </>
  );
}
